import React, { useEffect, useRef } from "react";
import { track, setTag } from "../../../analytics";
import "./Tarifs.scss";
import TarifsCards from "./TarifsCards/TarifsCards";
import TarifsSkeleton from "./TarifsSkeleton/TarifsSkeleton";
import { useIsMobile } from "./useIsMobile";

const ACTIONS_HASH = "#tarifs-actions";
const ACTIONS_TAB = "акции";
// Та же фикс-шапка, что учитывает мобильное меню при скролле к якорю
const HEADER_OFFSET = 86;

const Tarifs = ({ tarifsTitle, tarifCards,tarifIptvData,  tarifAdditionallyData,  tarifYearCards , tarifActionCards , activeTitle , setActiveTitle, tarifsLoaded = true}) => {
  /*
    Активный пилл доскролливается в видимую область мобильного ряда.

    Двигаем scrollLeft самого ряда, а не зовём scrollIntoView: тот
    прокручивает ВСЕХ прокручиваемых предков, включая страницу. Эффект
    срабатывает и на первом рендере, поэтому главная открывалась уже
    прокрученной на ~174px — герой уезжал под шапку, а лист тарифов
    оказывался посреди экрана (замерено трассировкой: scrollIntoView
    (tarifsTitle_text) на 80мс, 0 -> 174, во всех трёх сценариях
    открытия).
  */
  const activePillRef = useRef(null);
  useEffect(() => {
    const pill = activePillRef.current;
    const row = pill && pill.parentElement;
    if (!pill || !row) return;
    const GAP = 12; // запас, чтобы пилл не прилипал к краю ряда
    const left = pill.offsetLeft;
    const right = left + pill.offsetWidth;
    if (left < row.scrollLeft) {
      row.scrollLeft = Math.max(0, left - GAP);
    } else if (right > row.scrollLeft + row.clientWidth) {
      row.scrollLeft = right - row.clientWidth + GAP;
    }
  }, [activeTitle]);

  // Кол-во тарифов по категориям (индексы tarifsTitle). Пустая категория
  // скрывается на мобилке обобщённо, а не по имени.
  const isMobile = useIsMobile();
  const categoryCounts = [
    tarifCards?.length ?? 0,
    tarifYearCards?.length ?? 0,
    tarifIptvData?.length ?? 0,
    tarifAdditionallyData?.length ?? 0,
    tarifActionCards?.length ?? 0,
  ];
  // На мобилке таб виден только если в категории есть тарифы. Десктоп — всегда.
  // Пока ответ не пришёл, показываем все: иначе ряд пилюль дособирается
  // по приходу данных и двигает секцию.
  const isTabVisible = (index) =>
    !isMobile || !tarifsLoaded || (categoryCounts[index] ?? 0) > 0;

  // Активная категория на мобилке оказалась пустой/скрытой → первая непустая.
  useEffect(() => {
    if (!isMobile || !tarifsLoaded) return;
    if ((categoryCounts[activeTitle] ?? 0) > 0) return;
    const firstNonEmpty = categoryCounts.findIndex((c) => c > 0);
    if (firstNonEmpty !== -1 && firstNonEmpty !== activeTitle) setActiveTitle(firstNonEmpty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, activeTitle, tarifCards, tarifYearCards, tarifIptvData, tarifAdditionallyData, tarifActionCards]);

  // Пункт меню «Акции» ведёт на #tarifs-actions: скроллит сюда и включает
  // вкладку акций. Индекс берём из данных, а не числом — порядок категорий
  // может поменяться. Мобильное меню меняет хэш через replaceState и само
  // шлёт hashchange, десктопное — обычной ссылкой.
  useEffect(() => {
    const apply = () => {
      if (window.location.hash !== ACTIONS_HASH) return;
      const index = tarifsTitle?.findIndex(
        (e) => String(e?.name).trim().toLowerCase() === ACTIONS_TAB
      );
      if (index > -1) setActiveTitle(index);
      const section = document.getElementById("tarifs");
      if (!section) return;
      const top = section.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
      window.scrollTo({ top, behavior: "smooth" });
    };
    apply();
    window.addEventListener("hashchange", apply);
    return () => window.removeEventListener("hashchange", apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tarifsTitle]);

  return (
    <div className="tarifs_container" id="tarifs">
      {/* цель якоря «Акции»: мобильное меню скроллит по id, а не по хэшу */}
      <span id="tarifs-actions" aria-hidden="true" />
      <div className="tarifs_block">
        <h1>Выберите тариф для подключения</h1>
        <div className="tarifs_title">
          {tarifsTitle?.map((e, index) =>
            isTabVisible(index) ? (
              <div className="tarifsTitle_text" key={index} ref={activeTitle === index ? activePillRef : null}>
                <p
                  className={[activeTitle === index ? "active" : "", e.isNew ? "has-new" : ""]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => {
                    track("tariff_tab", { tab: e.name });
                    setTag("tariff_tab", e.name);
                    setActiveTitle(index);
                  }}
                >
                  {e.name}
                </p>
                {/* индикатор «новое»: точка внутри пилла (флаг isNew в tarifsTitle) */}
                {e.isNew ? (
                  <span className="tarifsTitle_new-dot" aria-hidden="true" />
                ) : null}
              </div>
            ) : null
          )}
        </div>
        <div className="tarifs_item">
          {!tarifsLoaded ? (
            <TarifsSkeleton />
          ) : (categoryCounts[activeTitle] ?? 0) === 0 ? (
            <p className="tarifs_empty">Тарифы скоро появятся</p>
          ) : (
            <TarifsCards tarifCards={tarifCards} tarifIptvData={tarifIptvData}  tarifAdditionallyData={tarifAdditionallyData} activeTitle={activeTitle} tarifYearCards={tarifYearCards} tarifActionCards={tarifActionCards}/>
          )}
        </div>
      </div>
      
    </div>
  );
};

export default Tarifs;
